/**
 * Normalized error type for the shared API layer (issue #1963, Phase 1).
 *
 * Every REST failure that flows through {@link api} is converted into an
 * `ApiError` so call sites get one stable shape to branch on — a
 * human-readable `message`, the HTTP `status`, the `requestId` captured by
 * `installRequestIdInterceptor` (issue #1446), the parsed response `data`
 * (for endpoints that return a structured error body), and a flag
 * distinguishing a benign abort/teardown from a real failure. The original
 * thrown value is preserved on `originalError` for logging.
 */

import axios from "axios";

import { getApiErrorMessage } from "@/core/utils/apiErrors";
import { isExplicitAbortError } from "@/utils/requestAbort";

import { NORMALIZED_ERROR, readErrorCode } from "./errorCodes";

export interface ApiErrorInit {
  message: string;
  code?: string;
  status?: number;
  requestId?: string;
  data?: unknown;
  aborted?: boolean;
  originalError?: unknown;
}

export class ApiError extends Error {
  /**
   * Marks {@link code} as a normalized error code, so `readErrorCode` can read
   * it without importing this class and without having to guess from the value
   * — axios puts an unrelated `code` on the same property name.
   */
  readonly [NORMALIZED_ERROR] = true;
  /**
   * The machine-readable discriminator: what the failure *was*, as opposed to
   * what to tell the user about it. Populated for every transport (see
   * {@link readErrorCode}); branch on this, never on {@link message}.
   *
   * Not restricted to the transport vocabulary: a domain code (#3131) or one
   * the backend adds is carried through unchanged.
   */
  readonly code?: string;
  /** HTTP status code, when the failure reached the server. */
  readonly status?: number;
  /** `X-Request-Id` captured off the response, for correlating with server logs. */
  readonly requestId?: string;
  /**
   * Parsed response body from the failed request, when the server returned one.
   * Lets callers branch on a structured error payload (e.g. a partial result
   * delivered alongside a 4xx) without reaching back into axios internals.
   */
  readonly data?: unknown;
  /** True when the request was deliberately aborted or torn down (benign). */
  readonly aborted: boolean;
  /** The raw value originally thrown (axios error, DOMException, etc.). */
  readonly originalError?: unknown;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = "ApiError";
    this.code = init.code;
    this.status = init.status;
    this.requestId = init.requestId;
    this.data = init.data;
    this.aborted = init.aborted ?? false;
    this.originalError = init.originalError;
    // Restore the prototype chain so `instanceof ApiError` survives transpilation.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * Convert any thrown value into an {@link ApiError}. Idempotent — an existing
 * `ApiError` is returned unchanged. Pulls the server-provided message via
 * {@link getApiErrorMessage}, the status and response body off the axios
 * response, the `requestId` attached to the error by the request-id
 * interceptor, and the machine-readable {@link ApiError.code} via
 * {@link readErrorCode}.
 *
 * The code is read for *every* thrown shape, not just axios: a GraphQL failure
 * that rejects rather than resolving arrives as an `ApolloError`, carrying its
 * code on `graphQLErrors[].extensions` or on the `GraphQLTransportError`
 * envelope under `networkError`. Those used to normalize to a bare message,
 * which is why four stores grew their own `isExpectedAuthError` reading
 * `graphQLErrors` directly (#3142).
 */
export function toApiError(error: unknown, fallbackMessage = "Something went wrong. Please try again."): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  // Axios rejects a signal-aborted request with `CanceledError`
  // (`code: ERR_CANCELED`), not a DOM `AbortError`, so check both: the abort
  // helper covers raw fetch/DOM aborts, `axios.isCancel` covers axios cancels.
  // Without the `isCancel` arm an intentional unmount/cancel would normalize
  // to `aborted=false` and `handleApiError` would log + toast a benign cancel.
  const aborted = isExplicitAbortError(error) || axios.isCancel(error);
  const message = getApiErrorMessage(error, fallbackMessage);

  let status: number | undefined;
  let requestId: string | undefined;
  let data: unknown;
  if (axios.isAxiosError(error)) {
    status = error.response?.status;
    data = error.response?.data;
    const tagged = error as { requestId?: unknown };
    if (typeof tagged.requestId === "string") {
      requestId = tagged.requestId;
    }
  }

  const code = readErrorCode(error);

  return new ApiError({ message, code, status, requestId, data, aborted, originalError: error });
}
